import { XIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { KarlRailContent } from '@shared/lib/appShell';
import { Avatar, ProgressBar } from '@shared/ui';

import { focusKarlInput } from './focusKarlInput';

import styles from './KarlRail.module.css';

/**
 * Запрос оболочки открыть лист Karl (FE-586, FE-587). `id` растёт с каждым
 * запросом: повторное нажатие открывает лист снова. `prefill` — начало
 * сообщения для пустого поля.
 */
export interface KarlOpenRequest {
  id: number;
  prefill?: string | undefined;
}

/**
 * Рельс Karl (FE-193, DESIGN_SYSTEM §5.4, референсы screen3–screen5).
 *
 * Karl не живёт на отдельной странице чата: он сопровождает весь путь, поэтому
 * рельс присутствует на всех экранах `/app/*`. **Наполнение зависит от стадии
 * заказа, факт присутствия — нет.**
 *
 * Содержимое — личность Karl и переписка, и больше ничего. Списка «Сейчас я
 * делаю для вас» здесь нет намеренно: он пересказывал трекер семи шагов из
 * шапки другими словами и отнимал у диалога верхнюю четверть колонки.
 *
 * **Своего поля ввода у рельса нет.** Ленту и поле приносит сам диалог
 * (`KarlDialogue` в `extra`): пока рельс держал ещё и собственный composer, на
 * экране оказывались два поля для одной переписки — и отправляло сообщение
 * только одно из них. Рельс — рамка вокруг диалога, а не второй чат.
 *
 * Маркировка «KI-Assistent» обязательна и стоит под именем всегда — это
 * требование прозрачности AI (MVP_PRD §26), а не подпись. Изображение Karl —
 * сгенерированный образ AI-помощника, а не фотография сотрудника, и без этой
 * подписи рядом оно не показывается (DESIGN_SYSTEM §5.4). Точки присутствия
 * и слова «online» нет: Karl не человек, который бывает на месте или нет.
 */
export function KarlRail(content: KarlRailContent) {
  const { t } = useTranslation();

  return (
    <div className={styles.rail}>
      <div className={styles.identity}>
        <Avatar variant="karl" size="lg" />
        <div className={styles.identityText}>
          <p className={styles.name}>{t('app.karl.name')}</p>
          {/* Маркировка AI: обязательна, не декоративна. */}
          <p className={styles.role}>{t('app.karl.role')}</p>
        </div>
      </div>

      {content.message ? (
        <div className={styles.bubble}>
          <p className={styles.bubbleText}>{content.message}</p>
          {content.messageTime ? (
            <p className={`${styles.bubbleTime} tabular`}>{content.messageTime}</p>
          ) : null}
        </div>
      ) : null}

      {content.progressPercent !== undefined ? (
        <div className={styles.progress}>
          <p className={styles.progressLabel}>{content.progressLabel ?? t('app.karl.progress')}</p>
          <ProgressBar
            percent={content.progressPercent}
            label={content.progressLabel ?? t('app.karl.progress')}
            showValue
          />
        </div>
      ) : null}

      {content.extra}
    </div>
  );
}

/**
 * Мобильный вариант: до 1024 px рельс сворачивается в закреплённую снизу
 * кнопку-пузырь, открывающую панель-лист (DESIGN_SYSTEM §4.4).
 *
 * `docked` — тот же пузырь внутри полосы оболочки рядом с переходом к
 * следующему шагу (FE-585): он не плавает поверх страницы, а стоит в строке
 * полосы. Маркировка «KI-Assistent» остаётся полной и здесь — короткое «KI»
 * рядом с фотореалистичным лицом не выполняло бы требования §5.4.
 */
export function KarlRailMobile({
  docked = false,
  openRequest,
  ...content
}: KarlRailContent & {
  docked?: boolean;
  /**
   * Запрос открыть лист и поставить фокус в поле (FE-586, «Mit Karl sprechen»;
   * FE-587, «Suche anpassen» — с началом сообщения). `undefined` — запроса
   * этому пузырю нет.
   */
  openRequest?: KarlOpenRequest | undefined;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Отложенный фокус: поле появится только вместе с листом. Хранит и начало
  // сообщения, которое встанет в пустое поле.
  const [focusPending, setFocusPending] = useState<{ prefill?: string | undefined } | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Синхронизация с запросом оболочки — внешним по отношению к пузырю событием.
  // Оболочка создаёт новый объект на каждый запрос и держит прежний, пока
  // нового нет: повторное нажатие открывает лист снова, а перерисовка — нет.
  useEffect(() => {
    if (openRequest === undefined) return;
    setOpen(true);
    setFocusPending({ prefill: openRequest.prefill });
  }, [openRequest]);

  // Фокус — после того как лист отрисован: поле ввода живёт внутри него.
  useEffect(() => {
    if (!open || focusPending === null) return;
    setFocusPending(null);
    focusKarlInput(sheetRef.current, focusPending.prefill);
  }, [open, focusPending]);

  // Прокрутка фона блокируется, пока лист открыт: иначе страница уезжает под ним.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <div className={styles.mobile}>
      <button
        type="button"
        className={
          docked ? `${styles.bubbleButton} ${styles.bubbleButtonDocked}` : styles.bubbleButton
        }
        aria-expanded={open}
        aria-controls="karl-sheet"
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <Avatar variant="karl" size="sm" />
        <span className={styles.bubbleButtonText}>
          <span className={styles.bubbleButtonName}>{t('app.karl.name')}</span>
          <span className={styles.bubbleButtonRole}>{t('app.karl.role')}</span>
        </span>
      </button>

      {open ? (
        <div
          ref={sheetRef}
          className={styles.sheet}
          id="karl-sheet"
          role="dialog"
          aria-label={t('app.karl.name')}
        >
          <button
            type="button"
            className={styles.sheetClose}
            onClick={() => {
              setOpen(false);
            }}
            aria-label={t('common.close')}
          >
            <XIcon size={18} weight="bold" aria-hidden="true" />
          </button>
          <KarlRail {...content} />
        </div>
      ) : null}
    </div>
  );
}
